import { useState } from 'react'
import { useConfig, usePosScriptMutations, usePosScripts } from '@/api/queries'
import type { PosScript } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton, LinkButton } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Icons } from '@/components/ui/Icons'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { policyKindLabel } from '@/features/settings/policyTarget'
import { PosPolicyDialog } from '@/features/settings/PosPolicyDialog'
import { SettingsTableDialog } from '@/features/settings/SettingsTableDialog'
import { PosScriptEditor } from './PosScriptEditor'

type Editing = { script: PosScript | null; content?: string }

const size = (content: string | null) => (content ? `${(content.length / 1024).toFixed(1)} KB` : null)

/** Scraping scripts per point of sale, with validation before save, a one-step rollback and a link to each POS policy. */
export function PosScriptsDialog({ onClose }: { onClose: () => void }) {
  const { data, isLoading, error } = usePosScripts(true)
  const { restore } = usePosScriptMutations()
  const { data: config } = useConfig()
  const toast = useToast()
  const [editing, setEditing] = useState<Editing | null>(null)
  const [restoring, setRestoring] = useState<PosScript | null>(null)
  const [policyOf, setPolicyOf] = useState<PosScript | null>(null)
  const [policyTable, setPolicyTable] = useState<string | null>(null)

  const runRestore = async (script: PosScript) => {
    setRestoring(null)
    try {
      await restore.mutateAsync({ posId: script.posId, posName: script.posName })
      toast.notify(`${script.posName}: previous version restored`, 'success')
    } catch (err) {
      toast.error(err, 'Restore failed')
    }
  }

  const columns: Column<PosScript>[] = [
    { key: 'posId', header: 'POS id', align: 'right', width: '80px' },
    { key: 'posName', header: 'Point of sale', render: (s) => <strong>{s.posName}</strong> },
    { key: 'currentVersion', header: 'Current', value: (s) => size(s.currentVersion), render: (s) => (s.currentVersion ? <Badge tone="ok">{size(s.currentVersion)}</Badge> : <Badge>none</Badge>) },
    { key: 'previousVersion', header: 'Previous', value: (s) => size(s.previousVersion), render: (s) => (s.previousVersion ? <Badge>{size(s.previousVersion)}</Badge> : '–') },
    {
      key: 'actions',
      header: '',
      sortable: false,
      width: '150px',
      render: (s) => (
        <span className="u-row">
          <IconButton label={`Open the ${policyKindLabel(s.posId)} policy of ${s.posName}`} onClick={() => setPolicyOf(s)}>
            <Icons.Settings size={14} />
          </IconButton>
          <IconButton label="Edit current version" onClick={() => setEditing({ script: s })}>
            <Icons.Edit size={14} />
          </IconButton>
          <IconButton label="Open previous version" disabled={!s.previousVersion} onClick={() => setEditing({ script: s, content: s.previousVersion ?? '' })}>
            <Icons.History size={14} />
          </IconButton>
          <IconButton label="Restore previous version" disabled={!s.previousVersion || restore.isPending} onClick={() => setRestoring(s)}>
            <Icons.Sync size={14} />
          </IconButton>
        </span>
      ),
    },
  ]

  return (
    <Modal
      title="PoS scripts"
      onClose={onClose}
      size="lg"
      headerExtra={
        <div className="u-row u-wrap">
          {config?.links.posScriptsDocumentation && (
            <LinkButton size="sm" href={config.links.posScriptsDocumentation} icon={<Icons.External size={12} />}>
              Docs
            </LinkButton>
          )}
          <Button size="sm" onClick={() => setPolicyTable('channel-policies')} title="Scripts only run for channels whose policy uses the scraper data provider">
            Channel policies
          </Button>
          <Button size="sm" onClick={() => setPolicyTable('booking-engine-policies')} title="Booking engines are channel 17; their scripts are configured here">
            Booking engine policies
          </Button>
          <Button size="sm" variant="primary" icon={<Icons.Plus size={12} />} onClick={() => setEditing({ script: null })}>
            New script
          </Button>
        </div>
      }
    >
      <DataTable
        columns={columns}
        rows={data}
        rowKey={(s) => s.id}
        loading={isLoading}
        error={error}
        searchable
        searchPlaceholder="Search by POS id or name"
        dense
        initialSort={{ key: 'posName', direction: 'asc' }}
        emptyMessage="No script stored"
      />
      {editing && <PosScriptEditor script={editing.script} initialContent={editing.content} onClose={() => setEditing(null)} />}
      {policyOf && <PosPolicyDialog channelId={policyOf.posId} name={policyOf.posName} onClose={() => setPolicyOf(null)} />}
      {policyTable && <SettingsTableDialog tableKey={policyTable} onClose={() => setPolicyTable(null)} />}
      {restoring && (
        <ConfirmDialog
          title={`Restore ${restoring.posName}`}
          message="The previous version becomes the current one again. The version being replaced is kept as previous."
          confirmLabel="Restore"
          onCancel={() => setRestoring(null)}
          onConfirm={() => void runRestore(restoring)}
        />
      )}
    </Modal>
  )
}
